import { Trans, useLingui } from '@lingui/react/macro'
import { createFileRoute } from '@tanstack/react-router'
import { RedemptionHistoryContent } from '@/features/redemption-history/redemption-history-content'
import { useRedemptionHistoryInfiniteQuery } from '@/features/redemption-history/use-redemption-history-infinite-query/use-redemption-history-infinite-query'
import { useMemo, useState } from 'react'
import { Redemption } from '@/apis/pay-redemptions'
import { HistoryFilters } from '@/features/history/history-filters/history-filters'
import { formatISO } from 'date-fns'
import {
  RedemptionFiltersContent,
  RedemptionFiltersValues,
} from '@/features/redemption-history/redemption-filters/redemption-filters-content'
import { assertCanPlayBeforeLoad } from '@/features/ctrls-and-caps/assert-can-play-before-load'
import { TertiaryPageLayout } from '@/routes/-tertiary-page-layout'

type RedemptionsMap = Record<string, Redemption[]>

export const Route = createFileRoute('/_app/redemption-history')({
  component: RedemptionHistory,
  beforeLoad: async () => {
    return assertCanPlayBeforeLoad()
  },
})

function RedemptionHistory() {
  const { i18n, t } = useLingui()
  const [filters, setFilters] = useState<RedemptionFiltersValues | null>(null)
  const { to, from } = filters || {}

  const {
    data,
    isLoading,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useRedemptionHistoryInfiniteQuery({
    query:
      from && to
        ? {
            to: formatISO(to),
            from: formatISO(from),
          }
        : undefined,
  })

  const redemptionsGroupedByDate = useMemo(() => {
    const redemptionsDatedGroupsMap: RedemptionsMap =
      data?.pages
        ?.flatMap((page) => page.redemptions)
        .reduce((acc: RedemptionsMap, redemption) => {
          const requestedAt = new Date(
            redemption.requestedAt,
          ).toLocaleDateString(i18n.locale, {
            dateStyle: 'medium',
          })

          if (!acc[requestedAt]) {
            acc[requestedAt] = []
          }
          acc[requestedAt].push(redemption)

          return acc
        }, {}) || {}

    return Object.keys(redemptionsDatedGroupsMap).map((key) => ({
      title: key,
      redemptions: redemptionsDatedGroupsMap[key],
    }))
  }, [data, i18n])

  const isHeaderContentVisible = filters
    ? true
    : !isLoading && !isError && redemptionsGroupedByDate.length > 0

  return (
    <TertiaryPageLayout headerText={t`Redemption History`}>
      <header className="flex flex-col bg-white">
        {isHeaderContentVisible ? (
          <div
            className="my-2 flex items-center justify-between px-4 font-display leading-6 font-medium text-content-tertiary"
            data-testid="redemption-history-header-content"
          >
            <Trans>All Redemptions</Trans>
            <HistoryFilters
              filtersCount={filters ? 1 : 0}
              filters={filters}
              title={t`Purchase Filters`}
              description={t`
                    Filter your purchase history by selecting a specific date
                    range to narrow down the results.
                  `}
              content={
                <RedemptionFiltersContent
                  filters={filters}
                  onShowAllResults={setFilters}
                />
              }
            />
          </div>
        ) : null}
      </header>

      <RedemptionHistoryContent
        groupedRedemptions={redemptionsGroupedByDate}
        isError={isError}
        isLoading={isLoading}
        filters={filters}
        refetch={refetch}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
      />
    </TertiaryPageLayout>
  )
}
